import React, { ReactElement } from 'react'
import CreatableSelect, { CreatableProps } from 'react-select/creatable'
import Flex from 'typescript-styled-flex'
import { FiSettings } from 'react-icons/fi'
import FormElement from '../FormElement'
import { IFormElementProps } from '../FormElement/FormInput'
import {
  customDropdownIndicator,
  customOption,
  customSingleValue,
} from './customSelectComponents'
import { customStyles } from './customStyles'
import { IOptionProps } from './Option'

const CreatableSelectInput = FormElement(
  ({
    children,
    name,
    value,
    onChange,
    errorMsg,
    className,
    noOptionsMessage,
    placeholder,
    onIconClick,
    size = 1,
    createOption,
    customCreateMessage,
    isSmallReservation,
    isValidCreateOption,
    menuPortalTarget,
    menuPlacement,
    zIndex,
    createOptionPosition,
    withPopoverIcon,
    openMenuOnClick,
    onBlurInput: onBlur,
  }: ICreatableSelectProps) => {
    function selectOnlyValue(onChange: (event: any) => void) {
      return function(values: IChangeValues | IChangeValues[], actionMeta) {
        if (actionMeta.action === 'create-option' && createOption) {
          createOption(values)
        }
        if (Array.isArray(values)) {
          const onlyValues = values.map(v => v.value)
          return onChange(onlyValues)
        }

        return onChange(values.value)
      }
    }

    const values = React.Children.map(
      children,
      (child: ReactElement<IOptionProps & HTMLOptionElement>) => ({
        icon: child.props.icon,
        label: child.props.children,
        value: child.props.value,
      }),
    )

    const valuesAsArray: React.ReactNode[] = Array.isArray(value)
      ? value
      : [value]
    const selectedValue = values.filter(element =>
      valuesAsArray.includes(element.value),
    )
    const [defaultValue] = values

    function customNoOptionMessage() {
      return noOptionsMessage
    }

    function customCreateValue() {
      return customCreateMessage
    }

    return (
      <Flex
        full={true}
        justifyEnd={true}
        alignCenter={true}
        style={{ position: 'relative' }}
      >
        <CreatableSelect
          createOptionPosition={createOptionPosition}
          openMenuOnClick={openMenuOnClick}
          menuPortalTarget={menuPortalTarget}
          menuPlacement={menuPlacement}
          className={className}
          onBlur={onBlur}
          components={{
            DropdownIndicator: customDropdownIndicator(size),
            Option: customOption(size),
            SingleValue: customSingleValue(size),
          }}
          options={values}
          name={name}
          value={selectedValue || defaultValue}
          styles={{
            ...customStyles(
              isSmallReservation,
              !!errorMsg,
              null,
              null,
              null,
              size,
              zIndex,
            ),
            container: () => ({
              width: '100%',
            }),
            dropdownIndicator: base => ({
              display: base.display,
              marginRight: `${25 * size}px`,
            }),
          }}
          onChange={selectOnlyValue(onChange)}
          formatCreateLabel={customCreateValue}
          noOptionsMessage={customNoOptionMessage}
          placeholder={placeholder}
          isValidNewOption={isValidCreateOption}
        />

        <Flex
          onClick={onIconClick}
          style={{
            cursor: 'pointer',
            position: 'absolute',
            right: `${10 * size}px`,
          }}
        >
          <FiSettings size={16 * size} />
        </Flex>
      </Flex>
    )
  },
)

export { CreatableSelectInput }

export interface ICreatableSelectProps extends IFormElementProps {
  children: React.ReactNode
  value?: React.ReactNode | React.ReactNode[]
  placeholder?: string
  createOptionPosition?: any
  noOptionsMessage?: string
  customCreateMessage?: string
  onIconClick?: () => void
  createOption?: (option) => void
  menuPortalTarget?: any
  menuPlacement?: any
  zIndex?: number
  onBlurInput?: any
  isSmallReservation?: boolean
  openMenuOnClick?: any
  isValidCreateOption?: CreatableProps<IOptionProps>['isValidNewOption']
}

export interface IChangeValues {
  icon: string | JSX.Element
  label: HTMLCollection
  value: string
}
